// routes/search.$tagId.tsx
import {
	json,
	type LoaderFunctionArgs,
	type ActionFunctionArgs,
} from '@remix-run/node'
import { useLoaderData, useSearchParams, useSubmit } from '@remix-run/react'
import { useState } from 'react'
import ResourceCard from '#app/components/Resource.js'
import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
	PaginationEllipsis,
} from '#app/components/ui/pagination'
import { requireUserId } from '#app/utils/auth.server.ts'
import { cachified, cache } from '#app/utils/cache.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { usePagination } from '#app/utils/pagination'

const RESOURCE_PAGE_COUNT = 12

const getResourcesWithTag = async (
	tagId: string,
	searchParams: URLSearchParams,
) => {
	const skip = parseInt(searchParams.get('skipResource') || '1')
	const isNotSafe = searchParams.get('notSafe') === 'on'

	return await cachified({
		key: `db-tagged-resources:${tagId}:${skip}:${isNotSafe}`,
		cache,
		ttl: 1000 * 60 * 60,
		staleWhileRevalidate: 1000 * 60 * 60 * 24,
		getFreshValue: async () => {
			const tag = await prisma.tag.findUniqueOrThrow({
				where: { id: tagId },
				select: {
					id: true,
					name: true,
					Tagging: {
						skip: (skip - 1) * RESOURCE_PAGE_COUNT,
						take: RESOURCE_PAGE_COUNT,
						select: {
							Resource: {
								select: {
									id: true,
									title: true,
									location: true,
									institution: true,
									Artist: { select: { name: true } },
									favorites: { select: { id: true } },
									path: true,
								},
							},
						},
					},
					_count: true,
				},
			})
			return { tag }
		},
	})
}

export async function loader({ params, request }: LoaderFunctionArgs) {
	const { tagId } = params
	if (!tagId) throw new Error('No tagId provided')
	const searchParams = new URL(request.url).searchParams
	return json(await getResourcesWithTag(tagId, searchParams))
}

export async function action({ request, params }: ActionFunctionArgs) {
	const userId = await requireUserId(request)
	const { tagId } = params
	if (!tagId) throw new Error('No tagId provided')

	const url = new URL(request.url)
	const skipResource = url.searchParams.get('skipResource') || '1'
	const isNotSafe = url.searchParams.get('notSafe') === 'on'

	const formData = await request.formData()
	checkHoneypot(formData)

	const resourceId = formData.get('resourceId') as string
	const submitType = formData.get('type')
	const favoriteId = formData.get('favoriteId') as string | null

	// Invalidate cache for this tag page
	const cacheKey = `db-tagged-resources:${tagId}:${skipResource}:${isNotSafe}`
	await cache.delete(cacheKey)

	switch (submitType) {
		case 'like': {
			const like = await prisma.favorite.create({
				data: {
					userId,
					resourceId: parseInt(resourceId),
				},
			})
			return json({
				data: {
					resourceId: parseInt(resourceId),
					favoriteId: like.id,
				},
			})
		}
		case 'dislike': {
			if (!favoriteId) {
				throw new Error('favoriteId is required for dislike')
			}
			await prisma.favorite.delete({
				where: { id: favoriteId },
			})
			return json({
				data: {
					resourceId: parseInt(resourceId),
					favoriteId: null,
				},
			})
		}
		default:
			return json({ data: null })
	}
}

export default function TagResourcePage() {
	const { tag } = useLoaderData<typeof loader>()
	const [searchParams] = useSearchParams()
	const submit = useSubmit()
	const current_page = parseInt(searchParams.get('skipResource') || '1')
	const total_pages = Math.ceil(tag._count.Tagging / RESOURCE_PAGE_COUNT)
	const pagination = usePagination({ current_page, total_pages })

	const [selectedResource, setSelectedResource] = useState<null | number>(null)
	const [, setValue] = useState(searchParams.get('search') || '')

	const handlePageChange = (page: number) => {
		const params = new URLSearchParams(searchParams)
		params.set('skipResource', String(page))
		submit(params, { method: 'get', replace: true })
	}

	return (
		<div className="mx-auto max-w-screen-lg">
			<div className="mx-8 sm:mx-2">
				<h2 className="mb-4 text-xl font-semibold">
					Artwork results for: {tag.name}
				</h2>
				<div className="grid-cols-full grid max-w-screen-lg grid-flow-dense gap-2 sm:grid-cols-2 lg:grid-cols-fluid">
					{tag.Tagging.map(item => (
						<ResourceCard
							key={item.Resource.id}
							resource={item.Resource}
							selectedResource={selectedResource}
							setSelectedResource={setSelectedResource}
							setValue={setValue}
							notSafe={searchParams.get('notSafe') === 'on'}
						/>
					))}
				</div>

				<Pagination className="my-6">
					<PaginationContent className="flex flex-wrap justify-center space-x-2 md:space-x-4 lg:space-x-6">
						{current_page > 1 && (
							<PaginationItem>
								<PaginationPrevious
									onClick={() => handlePageChange(current_page - 1)}
								/>
							</PaginationItem>
						)}
						{pagination.map((page, idx) =>
							page === 'DOTS' ? (
								<PaginationItem key={idx}>
									<PaginationEllipsis />
								</PaginationItem>
							) : (
								<PaginationItem key={idx}>
									<PaginationLink
										isActive={page === current_page}
										onClick={() => handlePageChange(page)}
									>
										{page}
									</PaginationLink>
								</PaginationItem>
							),
						)}
						{current_page < total_pages && (
							<PaginationItem>
								<PaginationNext
									onClick={() => handlePageChange(current_page + 1)}
								/>
							</PaginationItem>
						)}
					</PaginationContent>
				</Pagination>
			</div>
		</div>
	)
}
